import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ServiceCards.css';

interface Service {
  id: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  gradient: string;
  subServiceCount: number;
}

const ServiceCards: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const services: Service[] = [
    {
      id: 1,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Shiva Lingam with Trishul */}
          <ellipse cx="50" cy="70" rx="15" ry="20" fill="currentColor" opacity="0.8"/>
          <ellipse cx="50" cy="65" rx="12" ry="8" fill="currentColor"/>
          <path d="M50 20 L45 35 L50 40 L55 35 Z" fill="currentColor"/>
          <line x1="50" y1="20" x2="50" y2="40" stroke="currentColor" strokeWidth="2"/>
          <line x1="45" y1="25" x2="55" y2="25" stroke="currentColor" strokeWidth="2"/>
          <circle cx="50" cy="45" r="3" fill="currentColor"/>
          <path d="M35 75 Q50 65 65 75" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: t('home.services.shivaPooja.title'),
      description: t('home.services.shivaPooja.description'),
      link: '/service/shiv-pujan',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      subServiceCount: 3
    },
    {
      id: 2,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Goddess Devi with Lotus */}
          <ellipse cx="50" cy="80" rx="20" ry="8" fill="currentColor" opacity="0.6"/>
          <path d="M50 20 Q45 25 40 35 Q45 45 50 50 Q55 45 60 35 Q55 25 50 20 Z" fill="currentColor"/>
          <circle cx="45" cy="30" r="2" fill="currentColor"/>
          <circle cx="55" cy="30" r="2" fill="currentColor"/>
          <path d="M40 60 Q50 55 60 60 Q55 70 50 75 Q45 70 40 60 Z" fill="currentColor"/>
          <path d="M30 50 Q35 45 40 50 L35 60 Q30 55 30 50 Z" fill="currentColor"/>
          <path d="M70 50 Q65 45 60 50 L65 60 Q70 55 70 50 Z" fill="currentColor"/>
          <circle cx="50" cy="35" r="1" fill="currentColor"/>
        </svg>
      ),
      title: t('home.services.deviPooja.title'),
      description: t('home.services.deviPooja.description'),
      link: '/service/devi-pujan',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      subServiceCount: 3
    },
    {
      id: 3,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Nine Planets arranged in circle */}
          <circle cx="50" cy="50" r="3" fill="currentColor"/>
          <circle cx="50" cy="25" r="2" fill="currentColor"/>
          <circle cx="70" cy="35" r="2" fill="currentColor"/>
          <circle cx="75" cy="50" r="2" fill="currentColor"/>
          <circle cx="70" cy="65" r="2" fill="currentColor"/>
          <circle cx="50" cy="75" r="2" fill="currentColor"/>
          <circle cx="30" cy="65" r="2" fill="currentColor"/>
          <circle cx="25" cy="50" r="2" fill="currentColor"/>
          <circle cx="30" cy="35" r="2" fill="currentColor"/>
          <circle cx="50" cy="15" r="35" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.3"/>
        </svg>
      ),
      title: t('home.services.navgrahPooja.title'),
      description: t('home.services.navgrahPooja.description'),
      link: '/service/navagrah-pujan',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      subServiceCount: 8
    },
    {
      id: 4,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Baby with Om symbol */}
          <circle cx="50" cy="35" r="15" fill="currentColor" opacity="0.7"/>
          <circle cx="45" cy="32" r="2" fill="currentColor"/>
          <circle cx="55" cy="32" r="2" fill="currentColor"/>
          <path d="M45 38 Q50 42 55 38" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M30 60 Q50 55 70 60 Q65 75 50 80 Q35 75 30 60 Z" fill="currentColor" opacity="0.6"/>
          <path d="M40 25 Q50 15 60 25 Q55 30 50 28 Q45 30 40 25 Z" fill="currentColor"/>
          <text x="50" y="70" textAnchor="middle" fontSize="16" fill="currentColor">ॐ</text>
        </svg>
      ),
      title: t('home.services.vishist_sanskar.title'),
      description: t('home.services.vishist_sanskar.description'),
      link: '/service/vishist-sanskar',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      subServiceCount: 4
    },
    {
      id: 5,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Sacred Thread with hands */}
          <path d="M30 40 Q50 35 70 40" stroke="currentColor" strokeWidth="3" fill="none"/>
          <path d="M30 45 Q50 40 70 45" stroke="currentColor" strokeWidth="3" fill="none"/>
          <path d="M30 50 Q50 45 70 50" stroke="currentColor" strokeWidth="3" fill="none"/>
          <circle cx="35" cy="30" r="8" fill="currentColor" opacity="0.7"/>
          <circle cx="32" cy="28" r="1" fill="currentColor"/>
          <circle cx="38" cy="28" r="1" fill="currentColor"/>
          <path d="M20 55 L25 60 L30 55 L35 65 L30 70 L25 65 L20 70 Z" fill="currentColor"/>
          <path d="M65 55 L70 60 L75 55 L80 65 L75 70 L70 65 L65 70 Z" fill="currentColor"/>
          <circle cx="50" cy="75" r="3" fill="currentColor"/>
        </svg>
      ),
      title: t('home.services.shanti_samridhi.title'),
      description: t('home.services.shanti_samridhi.description'),
      link: '/service/shanti-samridhi',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      subServiceCount: 3
    },
    {
      id: 6,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Wedding couple with mandap */}
          <rect x="20" y="15" width="60" height="5" fill="currentColor"/>
          <rect x="18" y="15" width="4" height="40" fill="currentColor"/>
          <rect x="78" y="15" width="4" height="40" fill="currentColor"/>
          <circle cx="40" cy="35" r="6" fill="currentColor"/>
          <circle cx="60" cy="35" r="6" fill="currentColor"/>
          <path d="M35 45 Q40 40 45 45 L42 55 L38 55 Z" fill="currentColor"/>
          <path d="M55 45 Q60 40 65 45 L62 55 L58 55 Z" fill="currentColor"/>
          <circle cx="50" cy="65" r="8" fill="currentColor" opacity="0.6"/>
          <path d="M30 70 Q50 60 70 70" stroke="currentColor" strokeWidth="2" fill="none"/>
        </svg>
      ),
      title: t('home.services.dosh_nivaran.title'),
      description: t('home.services.dosh_nivaran.description'),
      link: '/service/dosh-nivaran',
      gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
      subServiceCount: 3
    },
    {
      id: 7,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Lord Vishnu with Chakra and Conch */}
          <circle cx="50" cy="40" r="12" fill="currentColor" opacity="0.7"/>
          <circle cx="46" cy="37" r="1.5" fill="currentColor"/>
          <circle cx="54" cy="37" r="1.5" fill="currentColor"/>
          <path d="M46 42 Q50 45 54 42" stroke="currentColor" strokeWidth="1" fill="none"/>
          <circle cx="30" cy="30" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M25 25 L35 35 M35 25 L25 35" stroke="currentColor" strokeWidth="2"/>
          <path d="M65 25 Q70 30 65 35 Q60 30 65 25 Z" fill="currentColor"/>
          <path d="M40 60 Q50 55 60 60 L58 75 L42 75 Z" fill="currentColor"/>
          <circle cx="50" cy="80" r="3" fill="currentColor"/>
        </svg>
      ),
      title: t('home.services.shradh_karm.title'),
      description: t('home.services.shradh_karm.description'),
      link: '/service/shradh-karm',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      subServiceCount: 4
    },
    {
      id: 8,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Book with Om and Hanuman symbol */}
          <rect x="25" y="30" width="50" height="40" rx="3" fill="currentColor" opacity="0.7"/>
          <rect x="30" y="25" width="40" height="45" rx="2" fill="currentColor"/>
          <line x1="35" y1="35" x2="65" y2="35" stroke="white" strokeWidth="2"/>
          <line x1="35" y1="42" x2="60" y2="42" stroke="white" strokeWidth="1"/>
          <line x1="35" y1="48" x2="65" y2="48" stroke="white" strokeWidth="1"/>
          <line x1="35" y1="54" x2="55" y2="54" stroke="white" strokeWidth="1"/>
          <circle cx="50" cy="15" r="8" fill="currentColor"/>
          <path d="M45 12 L50 8 L55 12 L52 18 L48 18 Z" fill="currentColor"/>
        </svg>
      ),
      title: t('home.services.ramayanSunderKand.title'),
      description: t('home.services.ramayanSunderKand.description'),
      link: '/service/ramayan-sunder-kand',
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      subServiceCount: 3
    },
    {
      id: 9,
      icon: (
        <svg width="32" height="32" viewBox="0 0 100 100" fill="currentColor">
          {/* Snake and Shiva Lingam for Kaal Sarp Dosha */}
          <ellipse cx="50" cy="60" rx="12" ry="15" fill="currentColor" opacity="0.8"/>
          <ellipse cx="50" cy="55" rx="9" ry="6" fill="currentColor"/>
          <path d="M30 30 Q40 20 50 25 Q60 20 70 30 Q60 40 50 35 Q40 40 30 30 Z" stroke="currentColor" strokeWidth="3" fill="none"/>
          <circle cx="35" cy="25" r="2" fill="currentColor"/>
          <circle cx="65" cy="25" r="2" fill="currentColor"/>
          <path d="M25 50 Q35 45 45 50 Q35 55 25 50 Z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M75 50 Q65 45 55 50 Q65 55 75 50 Z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="50" cy="45" r="2" fill="currentColor"/>
        </svg>
      ),
      title: t('home.services.custom_solutions.title'),
      description: t('home.services.custom_solutions.description'),
      link: '/contact',
      gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
      subServiceCount: 1
    }
  ];

  return (
    <section className="services-section" id="services">
      <div className="services-container">
        <div className="services-header">
          <h2>{t('home.servicesTitle')}</h2>
          <p>{t('home.servicesSubtitle')}</p>
        </div>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="service-card-link"
              onClick={() => navigate(service.link)}
            >
              <div 
                className={`service-card ${service.id === 9 ? 'custom-solutions-card' : ''}`}
                style={{ '--card-gradient': service.gradient } as React.CSSProperties}
                data-index={index}
              >
                {service.subServiceCount > 1 && (
                  <div className="sub-service-badge">
                    <span className="sub-service-count">{service.subServiceCount}</span>
                    <span className="sub-service-text">options</span>
                  </div>
                )}
                <div className="service-card-inner">
                  <div className="service-icon">
                    {service.icon}
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <div className="service-link">
                    <span>{service.id === 9 ? 'Contact Us' : 'Learn more'}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceCards;